import React, { useEffect, useRef, useState } from 'react'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'

import styles from './ClinicalProcessHeader.module.sass'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import CopyToClipboard from '@jimmyboys/liita-ui/dist/components/CopyToClipboard'
import { useInbox } from 'store'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'
import { prefixPlusSignIfLongerThan9Digits } from 'utils/computePhoneNumber'
import ManageCoordinatorButton from './ManageCoordinatorButton'
import { CoordinatorType } from 'utils/enums'

const GOOGLE_MAPS_LINK = 'https://www.google.com/maps/search/?api=1&query='

const getComplexity = (infoObj, i18n) => {
	let complexity = i18n`Process without triage`
	if (infoObj?.processType === 'PPC') {
		complexity = i18n`Poten. Complex Process`
	} else if (infoObj?.processType === 'PS') {
		complexity = i18n`Simple Process`
	} else if (infoObj?.processType === 'PRE') {
		complexity = i18n`Process in Preparation`
	}

	return complexity
}

const ClinicalProcessHeader = (props) => {
	const {
		className,
		processId,
		injured,
		accident,
		insurancePolicy,
		state,
		clinicalStatus,
		mainMedicalDiagnosis,
		provider,
		info,
		trustClaimsManager,
		companyClaimsManager,
		clinicCoordinator,
		initialCoordinator,
		ippCoordinator,
		tempId,
		category,
		participationFile,
		processTag,
		processTags,
		acutePhase,
		coordinatorAllocationHistory,
		lockedCoordinators,
		...attributes
	} = props
	const { screenSize, i18n, openFile, showDialog } = useGlobal()
	const { expandedInbox } = useInbox()
	const [expanded, setExpanded] = useState(false)
	const descriptionElem = useRef(null)
	const [multiLineDescription, setMultiLineDescription] = useState(false)
	const processComplexity = getComplexity(info, i18n)

	const labels = {
		pensioner: i18n`Pensioner`,
		acutePhase: i18n`Acute Phase`,
		pensionerDate: i18n`Pensioner date`,
		admTransport: i18n`Administrative Transport`,
		biologicalRisk: i18n`Biological Risk`,
		insuranceManagement: i18n`Insurance Management`,
		createdAt: i18n`CreatedAt`,
		admittedAt: i18n`Admitted at`,
		trustManager: i18n`Trust manager`,
		injuredStatus: i18n`Injured Status`,
		clinicalStatus: i18n`Clinical Status`,
		insuranceProviderUndefined: i18n`Insurance Provider Undefined`,
		providerNumber: i18n`Provider Nº`,
		temporaryNumber: i18n`Temporary Nº`,
		policy: i18n`Policy`,
		accidentDate: i18n`Accident Date`,
		seeMore: i18n`See more`,
		seeLess: i18n`See less`,
		admissionDate: i18n`Admission date`,
		partOfBody: i18n`Part of Body`,
		iPPCoordinator: i18n`IPP Coordinator`,
		clinicalConsultant: i18n`Clinical Consultant`,
		administrativeCoordinator: i18n`Administrative Coordinator`,
		age: i18n`Age`,
		occupation: i18n`Occupation`,
		contact: i18n`Contact`,
		address: i18n`Address`,
		processWithoutProvider: i18n`Process without provider`,
		accidentLocal: i18n`Accident local`,
		initial: i18n`InitialAbbreviation`,
	}

	useEffect(() => {
		if (!descriptionElem.current) return
		const checkMultiline = () => {
			const { scrollHeight, offsetHeight } = descriptionElem.current
			setMultiLineDescription(() => scrollHeight > offsetHeight)
		}

		window.addEventListener('resize', checkMultiline)
		checkMultiline()
		return () => {
			window.removeEventListener('resize', checkMultiline)
		}
	}, [descriptionElem, accident])

	const clinicalStatusColor = clinicalStatus?.value?.toLowerCase()
	let processInjuredStateColor = ''
	if (accident?.status?.description === 'Aceite') {
		processInjuredStateColor = 'accepted'
	} else if (accident?.status?.description === 'Recusado') {
		processInjuredStateColor = 'rejected'
	}

	const insuranceTempNumbers = [insurancePolicy?.externalId, tempId]

	const isLargeOrMediumWidthWithInboxOpened =
		(screenSize === 'large' || screenSize === 'medium') && expandedInbox

	return (
		<VStack {...attributes} className={styles['header-container']}>
			<VStack className={styles['top-header-container-titles']}>
				<HStack gap='xsmall'>
					<HStack>
						{processId && (
							<button
								className={styles['history-button']}
								onClick={() => {
									showDialog(
										'ProcessHistoryDialog',
										i18n`Process History`,
										{
											processId,
										},
										false,
										undefined,
										undefined,
										'center'
									).catch(() => null)
								}}
							>
								<Icon
									className={styles['text-icon']}
									width='14px'
									height='14px'
									name={'history'}
								/>
							</button>
						)}
						<h4>{processComplexity}</h4>
					</HStack>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: '4px',
						}}
					>
						{processTags?.map((pt) => (
							<h5 key={pt.id} className={styles['process-tag']}>
								{pt.tag.name}
							</h5>
						))}
						{accident?.isPensioner ? (
							<h5 className={styles['tag']}>
								{labels.pensioner}
							</h5>
						) : null}
						{info?.biologicalRisk ? (
							<h5 className={styles['tag']}>
								{labels.biologicalRisk}
							</h5>
						) : null}
						{info?.managementType === 'SEG' ? (
							<h5 className={styles['tag']}>
								{labels.insuranceManagement}
							</h5>
						) : null}
						{info?.admTransportApproval ? (
							<h5 className={styles['tag']}>
								{labels.admTransport}
							</h5>
						) : null}
					</div>
				</HStack>
				<HStack
					className={`${styles['top-header-container']} ${
						isLargeOrMediumWidthWithInboxOpened &&
						styles['--large-with-inbox-open']
					}`}
				>
					<small>
						{labels.admittedAt}{' '}
						{info?.admissionDate?.dateTimeFormat || '-'} |{' '}
						{labels.trustManager}: {trustClaimsManager?.name || '-'}
						{processId && (
							<ManageCoordinatorButton
								type={CoordinatorType.AdministrativeManager}
								currentCoordinator={trustClaimsManager}
								lockedCoordinators={lockedCoordinators}
							/>
						)}
						{lockedCoordinators?.[
							CoordinatorType.AdministrativeManager
						] && (
							<Tooltip
								className={styles['text-icon']}
								text={i18n`Coordinator is locked in process`}
								displayDelay={0}
							>
								<Icon
									width='14px'
									height='14px'
									name={'lock'}
								/>
							</Tooltip>
						)}
					</small>
					<HStack className={styles['top-header-right']}>
						<HStack className={styles['top-header-right-line']}>
							<div
								className={`${styles['status']} ${
									styles[
										`--injured-state-${processInjuredStateColor}`
									]
								}`}
							/>
							<h5>{labels.injuredStatus}</h5>
							<small>
								{accident?.status?.description || '-'}
							</small>
						</HStack>
						<HStack className={styles['top-header-right-line']}>
							<div
								className={`${styles['status']} ${
									styles[
										`--clinical-status-${clinicalStatusColor}`
									]
								}`}
							/>
							<h5>{labels.clinicalStatus}</h5>
							<small>{clinicalStatus?.description || '-'}</small>
							{processId && (
								<Tooltip
									text={i18n`State history`}
									displayDelay={0}
								>
									<Icon
										className={`${styles['history-icon']} ${styles['text-icon']}`}
										name='history'
										width='12px'
										height='12px'
										onClick={() => {
											showDialog(
												'StateHistoryDialog',
												i18n`State history`,
												{ processId },
												false,
												undefined,
												'center',
												'clinical-state-history'
											)
												.then(() => {})
												.catch(() => null)
										}}
									/>
								</Tooltip>
							)}
						</HStack>
					</HStack>
				</HStack>
			</VStack>
			<HStack
				className={`${styles['bottom-header-container']} ${
					isLargeOrMediumWidthWithInboxOpened &&
					styles['--large-with-inbox-open']
				}`}
			>
				<VStack
					className={`${styles['bottom-header-left']} ${
						expanded && styles['--expanded']
					} ${
						isLargeOrMediumWidthWithInboxOpened &&
						styles['--large-with-inbox-open']
					}`}
				>
					<VStack className={styles['bottom-header-left-title']}>
						<h2>{accident?.segment?.name}</h2>
						<div className={styles['insurance-company']}>
							<Icon
								onClick={() => openFile(participationFile)}
								className={styles['icon']}
								width='24px'
								height='24px'
								name='shield'
							/>
							<h3>
								{insurancePolicy?.insuranceCompany
									?.internalName ||
									labels.insuranceProviderUndefined}
							</h3>
						</div>
					</VStack>
					<VStack className={styles['insurance-details']}>
						<HStack className={styles['insurance-details-line']}>
							<h5>{labels.providerNumber}</h5>
							<CopyToClipboard
								text={i18n`Copy text`}
								copiedText={i18n`Text copied`}
								clipboardText={insuranceTempNumbers[0] || ''}
								displayArrow={true}
							>
								<small className={styles['clipboard']}>
									{insuranceTempNumbers[0] || '-'}
								</small>
							</CopyToClipboard>
						</HStack>
						<HStack className={styles['insurance-details-line']}>
							<h5>{labels.temporaryNumber}</h5>
							<CopyToClipboard
								text={i18n`Copy text`}
								copiedText={i18n`Text copied`}
								clipboardText={insuranceTempNumbers[1] || ''}
								displayArrow={true}
							>
								<small className={styles['clipboard']}>
									{insuranceTempNumbers[1] || '-'}
								</small>
							</CopyToClipboard>
						</HStack>
						<HStack className={styles['insurance-details-line']}>
							<h5>{labels.policy}</h5>
							<CopyToClipboard
								text={i18n`Copy text`}
								copiedText={i18n`Text copied`}
								clipboardText={insurancePolicy?.policyNumber}
								displayArrow={true}
							>
								<small className={styles['clipboard']}>
									{insurancePolicy?.policyNumber || ''}
								</small>
							</CopyToClipboard>
						</HStack>
						<HStack className={styles['insurance-details-line']}>
							<h5>{i18n`${insurancePolicy?.insuranceCompany?.shortName} Manager`}</h5>
							<small>{companyClaimsManager?.name || '-'}</small>
						</HStack>
					</VStack>
					<VStack className={styles['header-summary']}>
						<HStack className={styles['insurance-details-line']}>
							<h5>{labels.accidentDate}</h5>
							<small>
								{accident?.date?.dateTimeFormat || '-'}
							</small>
						</HStack>
						<HStack className={styles['insurance-details-line']}>
							<h5>{labels.accidentLocal}</h5>
							<small>{accident?.local || '-'}</small>
						</HStack>
						<div
							ref={descriptionElem}
							className={`${styles['summary']} ${
								expanded && styles['--expanded']
							}`}
						>
							<p>{accident?.description}</p>
						</div>
						{multiLineDescription && (
							<button
								className={styles['read-more']}
								onClick={() => setExpanded(!expanded)}
							>
								{expanded ? (
									<p>{labels.seeLess}</p>
								) : (
									<p>{labels.seeMore}</p>
								)}
							</button>
						)}
					</VStack>
					{participationFile && (
						<Tooltip
							text={i18n`Participation File`}
							displayDelay={0}
						>
							<button
								className={styles['attachment-button']}
								onClick={() => openFile(participationFile)}
							>
								<Icon
									className={styles['attachment-icon']}
									width='24px'
									height='24px'
									name='attachment'
								/>
							</button>
						</Tooltip>
					)}
				</VStack>
				<VStack
					className={`${styles['bottom-header-right']} ${
						isLargeOrMediumWidthWithInboxOpened &&
						styles['--large-with-inbox-open']
					}`}
				>
					<HStack className={styles['bottom-header-right-item']}>
						<Icon
							className={`${styles['icon']} ${styles['--blue']}`}
							width='24px'
							height='24px'
							name='account'
						/>
						<VStack
							className={
								styles['bottom-header-right-item-content']
							}
						>
							<HStack>
								<CopyToClipboard
									text={i18n`Copy text`}
									copiedText={i18n`Text copied`}
									clipboardText={injured?.name}
									displayArrow={true}
									alwaysLeft={true}
								>
									<h3 className={styles['clipboard']}>
										{injured?.name}
									</h3>
								</CopyToClipboard>
								{accident?.segment?.abbreviation === 'AD' && (
									<h3 className={styles['category']}>
										&nbsp;{`(${category || '-'})`}
									</h3>
								)}
							</HStack>
							<VStack
								className={
									styles[
										'bottom-header-right-item-content-lower'
									]
								}
							>
								<HStack
									className={styles['insurance-details-line']}
								>
									<h5>{labels.age}</h5>
									<small>
										{injured?.age && injured?.age >= 0
											? i18n`${injured?.age} y/o` +
											  ' (' +
											  injured?.birthday +
											  ')'
											: '-'}
									</small>
								</HStack>
								<HStack
									className={styles['insurance-details-line']}
								>
									<h5>{labels.occupation}</h5>
									<small>{injured?.profession || '-'}</small>
								</HStack>
								<HStack
									className={styles['insurance-details-line']}
								>
									<h5>{labels?.contact}</h5>
									<div>
										{injured?.contacts?.mobile && (
											<a
												href={prefixPlusSignIfLongerThan9Digits(
													injured.contacts.mobile
												)}
												id='contact-mobile-number'
												data-value={
													injured.contacts.mobile
												}
											>
												<small>
													{
														injured
															.formattedPhoneContacts
															.mobile
													}
												</small>
											</a>
										)}
										{injured?.contacts?.mobile &&
											injured?.contacts?.telephone && (
												<small> - </small>
											)}
										{injured?.contacts?.telephone && (
											<a
												href={prefixPlusSignIfLongerThan9Digits(
													injured.contacts.telephone
												)}
												id='contact-telephone-number'
												data-value={
													injured.contacts.telephone
												}
											>
												<small>
													{
														injured
															.formattedPhoneContacts
															.telephone
													}
												</small>
											</a>
										)}
									</div>
								</HStack>
								<HStack
									className={`${styles['insurance-details-line']} ${styles['--address']}`}
								>
									<h5>{labels.address}</h5>
									{injured?.localeWithPostalCode ? (
										<Tooltip
											text={injured?.address || null}
										>
											<a
												className={styles['go-to-link']}
												href={`${GOOGLE_MAPS_LINK}${injured?.postalCode}`}
												target='_blank'
												rel='noreferrer'
											>
												<small>
													{
														injured.localeWithPostalCode
													}
												</small>

												<Icon
													className={
														styles['link-icon']
													}
													name='link'
													width='15px'
													height='15px'
												/>
											</a>
										</Tooltip>
									) : (
										<small>-</small>
									)}
								</HStack>
								{accident?.isPensioner && (
									<HStack
										className={
											styles['insurance-details-line']
										}
									>
										<h5>{labels.pensionerDate}</h5>
										<small>
											{`${i18n`Since`} ${
												accident.pensionerDate
													?.shortDateFormat
											}` || '-'}
										</small>
									</HStack>
								)}
							</VStack>
						</VStack>
					</HStack>
					<HStack className={styles['bottom-header-right-item']}>
						<Icon
							className={`${styles['icon']} ${styles['--blue']}`}
							width='24px'
							height='24px'
							name='vital-sign'
						/>
						<VStack
							className={
								styles['bottom-header-right-item-content']
							}
						>
							{processId && provider?.id ? (
								<span>
									<h3 className={styles['provider-name']}>
										{provider?.name}
									</h3>
									<Tooltip
										text={i18n`Coordinators and provider history`}
										displayDelay={0}
									>
										<Icon
											className={`${styles['history-icon']} ${styles['text-icon']}`}
											name='history'
											width='12px'
											height='12px'
											onClick={() => {
												showDialog(
													'CoordinatorAndProviderHistoryDialog',
													i18n`Coordinators and provider history`,
													{
														processId,
														allocationCoordinatorType:
															'Clinical',
													},
													false,
													undefined,
													'center',
													'coordinator-provider-history'
												)
													.then(() => {})
													.catch(() => null)
											}}
										/>
									</Tooltip>
								</span>
							) : (
								<h3 className={styles['disabled-label']}>
									{labels.processWithoutProvider}
								</h3>
							)}
							<VStack
								className={
									styles[
										'bottom-header-right-item-content-lower'
									]
								}
							>
								<HStack
									className={styles['insurance-details-line']}
								>
									<h5>{labels.partOfBody}</h5>
									<small>{info?.bodyPartName || '-'}</small>
								</HStack>
								<HStack
									className={styles['insurance-details-line']}
								>
									<h5>{labels.iPPCoordinator}</h5>
									<small>{ippCoordinator?.name || '-'}</small>
									{lockedCoordinators?.[
										CoordinatorType
											.FinalSituationPrevisionCoordinator
									] && (
										<Tooltip
											text={i18n`Coordinator is locked in process`}
											displayDelay={0}
										>
											<Icon
												width='14px'
												height='14px'
												name={'lock'}
											/>
										</Tooltip>
									)}
								</HStack>
								<HStack
									className={styles['insurance-details-line']}
								>
									<h5>
										{labels.clinicalConsultant}{' '}
										{!clinicCoordinator?.name &&
										initialCoordinator?.name
											? ` (${labels.initial})`
											: ''}
									</h5>
									<small>
										{clinicCoordinator?.name ||
											initialCoordinator?.name ||
											'-'}
										{processId && (
											<ManageCoordinatorButton
												type={
													CoordinatorType.ClinicalCoordinator
												}
												currentCoordinator={
													clinicCoordinator
												}
												lockedCoordinators={
													lockedCoordinators
												}
											/>
										)}
									</small>
									{(lockedCoordinators?.[
										CoordinatorType.InitialCoordinator
									] ||
										lockedCoordinators?.[
											CoordinatorType.ClinicalCoordinator
										]) && (
										<Tooltip
											text={i18n`Coordinator is locked in process`}
											displayDelay={0}
										>
											<Icon
												width='14px'
												height='14px'
												name={'lock'}
											/>
										</Tooltip>
									)}
								</HStack>
							</VStack>
						</VStack>
					</HStack>
				</VStack>
			</HStack>
		</VStack>
	)
}

export default ClinicalProcessHeader
